import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ConfigService } from '@app/core/config/config.service';
import { AuthenticationInfoService } from '@app/core/guards/authentication-info.service';
import { AccessRequirement } from '@app/core/models/access-requirement.model';
import { GroupModel } from '@app/core/models/groups/group.model';
import { WorkflowDetailModel } from '@app/core/models/workflows/workflow-detail.model';
import { WorkflowStage } from '@app/core/models/workflows/workflow-stage';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import * as _ from 'lodash';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-workflow-base',
  templateUrl: './workflow-base.component.html',
  styleUrls: ['./workflow-base.component.scss'],
})
export class WorkflowBaseComponent implements OnInit, OnDestroy {
  @Input() goBackToWorkflowList = () => {};
  @Input() createWorkflow = (
    workflowDetail: WorkflowDetailModel
  ): Observable<any> => null;
  @Input() updateWorkflow = (
    workflowDetail: WorkflowDetailModel
  ): Observable<any> => null;
  @Input() activateWorkflow = (workflowId: number): Observable<any> => null;
  @Input() deactivateWorkflow = (workflowId: number): Observable<any> => null;
  @Input() validStages = (workflowStages: WorkflowStage[]) => '';

  @Input() stageValidationGroupFilter = (group: GroupModel) => true;
  @Input() operationalGroupFilter = (group: GroupModel) => true;
  @Input() comtabilityGroupFilter = (group: GroupModel) => true;

  @Input() displayComptabilitySection: boolean;
  @Input() displayStageTriggerCondition: boolean;
  @Input() displayOptionalCondition: boolean = false;

  workflowDetail: WorkflowDetailModel;
  operationalGroups: GroupModel[] = [];
  stageValidationGroupsCompleted: GroupModel[] = [];
  stageValidationGroups: GroupModel[] = [];
  nonErpGroups: GroupModel[];
  nonErpGroupSelectedItem: GroupModel[];
  operationalGroupSelectedItem: GroupModel[];
  isDetailMode: boolean;
  canBeModified: boolean;
  maxWorkflowValidationStages: number;

  unsubscribe$: Subject<boolean> = new Subject<boolean>();
  groupDropDownSettings: IDropdownSettings;

  isAdministrator : boolean = false;

  constructor(
    protected config: ConfigService,
    private route: ActivatedRoute,
    protected multiSelectModel: MultiSelectModel,
    private authenticationInfoService: AuthenticationInfoService
  ) {
    this.stageValidationGroups = [];
    this.stageValidationGroupsCompleted = [];
    this.nonErpGroups = [];
    this.workflowDetail = new WorkflowDetailModel();
    this.maxWorkflowValidationStages =
      this.config.environnement.workflowMaxAllowedStages;
  }

  ngOnInit() {
    this.isAdministrator = this.authenticationInfoService.authentificatedUser
    ? this.authenticationInfoService.authentificatedUser.hasGrant(
        AccessRequirement.AdministrationMembership
      )
    : false;

    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: Params) => (this.isDetailMode = !_.isNil(data.id)));

    this.route.data
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((resolverData: Params) => {
        if (_.isNil(resolverData.workflowDetail)) {
          this.canBeModified = !this.isDetailMode;
        } else {
          this.workflowDetail = resolverData.workflowDetail;
          
          this.canBeModified =
            !this.isDetailMode || this.workflowDetail.CanBeModified;
          this.nonErpGroupSelectedItem = [
            this.workflowDetail.NonERPComptabilityGroup,
          ];
          this.operationalGroupSelectedItem = [
            this.workflowDetail.OperationalGroup,
          ];
        }

        if (!_.isNil(resolverData.groups)) {
          const groups = resolverData.groups;

          const workflowSelectedStageValidationGroups = _.map(
            this.workflowDetail?.WorkflowStages.filter(
              (s) => s.ValidationGroup != null
            ),
            (s) => s.ValidationGroup.Id
          );
          this.stageValidationGroups = groups.filter(
            (g) =>
              this.stageValidationGroupFilter(g) &&
              !_.includes(workflowSelectedStageValidationGroups, g.Id)
          );
          this.stageValidationGroupsCompleted = groups.filter((g) =>
            this.stageValidationGroupFilter(g)
          );
          this.nonErpGroups = groups.filter((g) =>
            this.comtabilityGroupFilter(g)
          );
          this.operationalGroups = groups.filter((g) =>
            this.operationalGroupFilter(g)
          );
        }
      });

    this.groupDropDownSettings = this.multiSelectModel.getSettings(
      'Id',
      'Name',
      true,
      true,
      true
    );
  }

  onSelectGroup(
    selectedGroup: GroupModel,
    group: GroupModel,
    action: string,
    removeSelectedItemFromList: boolean = false
  ): void {
    switch (action) {
      case 'select':
        group.Id = selectedGroup.Id;
        group.Name = selectedGroup.Name;
        if (removeSelectedItemFromList) {
          this.stageValidationGroups = this.stageValidationGroups.filter(
            (g) => g.Id !== selectedGroup.Id
          );
        }
        break;
      case 'deselect':
        group.Id = null;
        group.Name = null;
        this.stageValidationGroups.push(selectedGroup);
        break;
    }
  }

  isToggleOptional(currentworkflowStage: WorkflowStage): boolean {
    return (
      !this.canBeModified ||
      currentworkflowStage.ValidationGroup == null ||
      (currentworkflowStage.ValidationGroup != null &&
        currentworkflowStage.ValidationGroup.Id != null)
    );
  }

  addStage(): void {
    const nextvalidationLevel = this.workflowDetail.WorkflowStages.length + 1;
    this.workflowDetail.WorkflowStages.push(
      new WorkflowStage(nextvalidationLevel)
    );
  }

  removeStage(workflowStage: WorkflowStage): void {
    this.workflowDetail.WorkflowStages =
      this.workflowDetail.WorkflowStages.filter((w) => w !== workflowStage);
    if (!_.isNil(workflowStage.ValidationGroup?.Id)) {
      this.stageValidationGroups.push(
        Object.assign({}, workflowStage.ValidationGroup)
      );
    }
    this.synchronizeValidationLevels();
  }

  onCancel(): void {
    this.goBackToWorkflowList();
  }

  synchronizeValidationLevels(): void {
    let levelIndex = 1;
    this.workflowDetail.WorkflowStages.forEach((stage: WorkflowStage) => {
      stage.Level = levelIndex++;
    });
    if (this.workflowDetail.WorkflowStages.length) {
      const lastStage = _.tail(this.workflowDetail.WorkflowStages)[0];
      if (!_.isNil(lastStage?.ThresholdAmount)) {
        lastStage.ThresholdAmount = null;
      }
    }
  }

  get displayStageAddButton(): boolean {
    return (
      this.workflowDetail.WorkflowStages.length <
        this.config.environnement.workflowMaxAllowedStages && this.canBeModified
    );
  }

  get currentMaxLevel(): number {
    if (
      _.isNil(this.workflowDetail.WorkflowStages) ||
      !this.workflowDetail.WorkflowStages.length
    ) {
      return 0;
    }

    const levels = this.workflowDetail.WorkflowStages.map((s) => s.Level);
    return Math.max(...levels);
  }

  onSave = () => {
    if (!this.workflowDetail.CanBeModified) {
      this.updateWorkflowState();
    } else {
      if (!this.isDetailMode) {
        this.createWorkflow(this.workflowDetail)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((_) => {
            this.goBackToWorkflowList();
          });
      } else {
        this.updateWorkflow(this.workflowDetail)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((_) => {
            this.goBackToWorkflowList();
          });
      }
    }
  };

  private updateWorkflowState(): void {
    if (this.workflowDetail.IsActive) {
      this.activateWorkflow(this.workflowDetail.Id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((_) => (this.workflowDetail.IsActive = true));
    } else {
      this.deactivateWorkflow(this.workflowDetail.Id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (_) => (this.workflowDetail.IsActive = false),
          (err) => {
            if (err.status === 403) {
              this.workflowDetail.CanBeDisabled = false;
            }
          }
        );
    }
  }

  get stageValidations(): string {
    return this.validStages(this.workflowDetail.WorkflowStages);
  }

  get isValidationStageValid(): boolean {
    const validation = this.stageValidations;
    return _.isNil(validation) || !validation.length;
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }
}
